import React from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { fromShortIdsByProjectToExperimentIds } from '@neptune/shared/entity-business-logic';
import { isEmptyShortIdsMap, ShortIdsByProject } from '@neptune/shared/entity-domain';
import {
  AttributeWidgetSource,
  isAttributePatternSource,
  isFloatSeriesSource,
  widgetAttributeResolver,
  WidgetSource,
} from '@neptune/shared/widgets-domain';

import config from 'config';

const CHART_MAX_METRICS_LIMIT = config.chartMaxMetricsLimit ?? 2_000;

type WidgetSourceAttributesResult = Pick<
  UseQueryResult<AttributeWidgetSource[] | undefined>,
  'data' | 'isFetching' | 'isLoading'
> & {
  fetchingFailed: boolean;
};

export function useWidgetSourceAttributes(
  sources: WidgetSource[],
  shortIdsByProject: ShortIdsByProject,
): WidgetSourceAttributesResult {
  const directFloatSeriesSources = React.useMemo(() => {
    return sources.filter(isFloatSeriesSource).filter((source) => !source.metadata.subproperty);
  }, [sources]);

  const dynamicSources = React.useMemo(() => {
    return sources.filter(isAttributePatternSource);
  }, [sources]);

  const projectIds = React.useMemo(() => {
    return [...shortIdsByProject.keys()];
  }, [shortIdsByProject]);

  const experimentIds = React.useMemo(() => {
    return fromShortIdsByProjectToExperimentIds(shortIdsByProject);
  }, [shortIdsByProject]);

  const dynamicSourcesEnabled =
    !isEmptyShortIdsMap(shortIdsByProject) && dynamicSources.length !== 0;

  const dynamicResult = useQuery({
    enabled: dynamicSourcesEnabled,
    queryKey: ['widget-sources', projectIds, experimentIds, dynamicSources],
    queryFn: async () =>
      widgetAttributeResolver.fromSources({ projectIds, experimentIds, sources: dynamicSources }),
    keepPreviousData: true,
  });

  if (dynamicSourcesEnabled) {
    return dynamicResult.data
      ? {
          ...dynamicResult,
          data: [...directFloatSeriesSources, ...dynamicResult.data].slice(
            0,
            CHART_MAX_METRICS_LIMIT,
          ),
          fetchingFailed: Boolean(dynamicResult.error),
        }
      : {
          ...dynamicResult,
          fetchingFailed: Boolean(dynamicResult.error),
        };
  }

  return {
    data: directFloatSeriesSources.slice(0, CHART_MAX_METRICS_LIMIT),
    isLoading: false,
    isFetching: false,
    fetchingFailed: false,
  };
}
